import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { confirmDialog } from 'primereact/confirmdialog';
import {
    getActas, addActa, subirArchivoActa, deleteActa,
    getDocumentos, addDocumento, deleteDocumento, getArchivoUrl,
} from '../service/ComunidadService';

// Pestaña "Actas y documentos" del popup de una asamblea: alta, consulta y baja
// de las actas (tabla Actas) y de los documentos (DocumentosComunidad con asambleaId)
// de esa asamblea puntual.

const ACCEPT = '.pdf,.jpg,.jpeg,.png';
const MAX_BYTES = 5 * 1024 * 1024;

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const extensionValida = (f) => /\.(pdf|jpe?g|png)$/i.test(f?.name || '');

const AsambleaArchivos = ({ asamblea, administracionId, barrioId, usuarioId, rolId, toast }) => {
    const [actas, setActas] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(false);

    const [tituloActa, setTituloActa] = useState('');
    const [archivoActa, setArchivoActa] = useState(null);
    const [subiendoActa, setSubiendoActa] = useState(false);

    const [tituloDoc, setTituloDoc] = useState('');
    const [archivoDoc, setArchivoDoc] = useState(null);
    const [subiendoDoc, setSubiendoDoc] = useState(false);

    const inputActaRef = useRef(null);
    const inputDocRef = useRef(null);
    const inputReemplazoRef = useRef(null);
    const actaAReemplazar = useRef(null);

    const avisar = (severity, detail) => toast.current?.show({ severity, summary: severity === 'error' ? 'Error' : severity === 'warn' ? 'Atención' : 'Éxito', detail, life: 3500 });

    const fetchArchivos = useCallback(async () => {
        if (!asamblea?.asambleaId) return;
        setLoading(true);
        try {
            const [a, d] = await Promise.all([
                getActas(administracionId, barrioId),
                getDocumentos(administracionId, barrioId, undefined, asamblea.asambleaId),
            ]);
            setActas((a.data || []).filter(x => x.asambleaId === asamblea.asambleaId));
            setDocumentos(d.data || []);
        } catch {
            avisar('error', 'No se pudieron cargar las actas y documentos');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asamblea?.asambleaId, administracionId, barrioId]);

    useEffect(() => { fetchArchivos(); }, [fetchArchivos]);

    const validarArchivo = (f) => {
        if (!f) return false;
        if (!extensionValida(f)) { avisar('warn', 'Solo se permiten archivos PDF, JPG o PNG'); return false; }
        if (f.size > MAX_BYTES) { avisar('warn', 'El archivo no puede superar los 5 MB'); return false; }
        return true;
    };

    // ─── Actas ────────────────────────────────────────────────────────────────

    const subirActa = async () => {
        if (!tituloActa.trim()) { avisar('warn', 'Ingresá el título del acta'); return; }
        if (!archivoActa) { avisar('warn', 'Seleccioná el archivo del acta'); return; }
        setSubiendoActa(true);
        try {
            await addActa({
                asambleaId: asamblea.asambleaId,
                titulo: tituloActa.trim(),
                contenido: '',
                barrioId,
                administracionId,
                usuarioCreacionId: usuarioId,
            }, archivoActa);
            setTituloActa('');
            setArchivoActa(null);
            avisar('success', 'Acta subida correctamente');
            fetchArchivos();
        } catch (err) {
            avisar('error', err?.response?.data?.errorDisplay || 'No se pudo subir el acta');
        } finally {
            setSubiendoActa(false);
        }
    };

    const elegirReemplazo = (acta) => {
        actaAReemplazar.current = acta;
        inputReemplazoRef.current?.click();
    };

    const reemplazarArchivoActa = async (file) => {
        const acta = actaAReemplazar.current;
        if (!acta || !validarArchivo(file)) return;
        try {
            await subirArchivoActa(acta.actaId, administracionId, file);
            avisar('success', 'Archivo del acta actualizado');
            fetchArchivos();
        } catch (err) {
            avisar('error', err?.response?.data?.errorDisplay || 'No se pudo subir el archivo');
        }
    };

    const eliminarActa = (acta) => {
        confirmDialog({
            message: `¿Eliminar el acta "${acta.titulo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteActa(acta.actaId, administracionId);
                    fetchArchivos();
                } catch {
                    avisar('error', 'No se pudo eliminar el acta');
                }
            },
        });
    };

    // ─── Documentos ───────────────────────────────────────────────────────────

    const subirDocumento = async () => {
        if (!tituloDoc.trim()) { avisar('warn', 'Ingresá el título del documento'); return; }
        if (!archivoDoc) { avisar('warn', 'Seleccioná el archivo'); return; }
        setSubiendoDoc(true);
        try {
            await addDocumento({
                titulo: tituloDoc.trim(),
                descripcion: '',
                categoria: 'Asamblea',
                barrioId,
                administracionId,
                usuarioId,
                asambleaId: asamblea.asambleaId,
            }, archivoDoc);
            setTituloDoc('');
            setArchivoDoc(null);
            avisar('success', 'Documento subido correctamente');
            fetchArchivos();
        } catch (err) {
            avisar('error', err?.response?.data?.errorDisplay || 'No se pudo subir el documento');
        } finally {
            setSubiendoDoc(false);
        }
    };

    const eliminarDocumento = (doc) => {
        confirmDialog({
            message: `¿Eliminar el documento "${doc.titulo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteDocumento(doc.documentoComunidadId, administracionId, usuarioId, rolId);
                    fetchArchivos();
                } catch {
                    avisar('error', 'No se pudo eliminar el documento');
                }
            },
        });
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    const linkArchivo = (url, nombre) => url
        ? <a href={getArchivoUrl(url)} target="_blank" rel="noreferrer" title={nombre} style={{ color: '#1570ef' }}>
              <i className="pi pi-file" style={{ marginRight: 4 }} />Ver
          </a>
        : <span style={{ color: '#98a2b3' }}>Sin archivo</span>;

    const selectorArchivo = (archivo, inputRef, onPick) => (
        <>
            <Button type="button" label={archivo ? 'Cambiar archivo' : 'Elegir archivo'} icon="pi pi-paperclip"
                className="p-button-sm p-button-outlined" onClick={() => inputRef.current?.click()} />
            <span style={{ fontSize: '0.8rem', color: archivo ? '#344054' : '#98a2b3', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {archivo ? archivo.name : 'PDF, JPG o PNG (máx. 5 MB)'}
            </span>
            <input ref={inputRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (validarArchivo(f)) onPick(f); e.target.value = ''; }} />
        </>
    );

    return (
        <div className="flex flex-column gap-4">
            {/* input oculto para reemplazar el archivo de un acta existente */}
            <input ref={inputReemplazoRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
                onChange={(e) => { reemplazarArchivoActa(e.target.files?.[0]); e.target.value = ''; }} />

            {/* ─── Actas ─────────────────────────────────────────────────── */}
            <div>
                <div style={seccionTitulo}>Actas</div>
                <div className="flex flex-wrap align-items-center gap-2 mb-2" style={formBox}>
                    <InputText value={tituloActa} onChange={(e) => setTituloActa(e.target.value)} placeholder="Título del acta"
                        className="p-inputtext-sm" style={{ flex: '1 1 220px' }} maxLength={300} />
                    {selectorArchivo(archivoActa, inputActaRef, setArchivoActa)}
                    <Button type="button" label="Subir acta" icon="pi pi-upload" className="p-button-sm" loading={subiendoActa} onClick={subirActa} />
                </div>
                <DataTable value={actas} loading={loading} showGridlines responsiveLayout="scroll" className="p-datatable-sm"
                    emptyMessage="Esta asamblea no tiene actas cargadas">
                    <Column field="titulo" header="Título" />
                    <Column header="Archivo" style={{ width: '9rem' }} body={(r) => linkArchivo(r.archivoUrl, r.archivoNombre)} />
                    <Column header="Cargada" style={{ width: '8rem' }} body={(r) => fmtFecha(r.fechaAlta)} />
                    <Column header="Acciones" style={{ width: '7rem', textAlign: 'center' }} body={(r) => (
                        <div className="flex gap-1 justify-content-center">
                            <Button icon="pi pi-upload" className="p-button-rounded p-button-text p-button-sm"
                                tooltip={r.archivoUrl ? 'Reemplazar archivo' : 'Subir archivo'} onClick={() => elegirReemplazo(r)} />
                            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger"
                                tooltip="Eliminar" onClick={() => eliminarActa(r)} />
                        </div>
                    )} />
                </DataTable>
            </div>

            {/* ─── Documentos ────────────────────────────────────────────── */}
            <div>
                <div style={seccionTitulo}>Documentos</div>
                <div className="flex flex-wrap align-items-center gap-2 mb-2" style={formBox}>
                    <InputText value={tituloDoc} onChange={(e) => setTituloDoc(e.target.value)} placeholder="Título del documento (ej: Presupuesto, Orden del día)"
                        className="p-inputtext-sm" style={{ flex: '1 1 220px' }} maxLength={300} />
                    {selectorArchivo(archivoDoc, inputDocRef, setArchivoDoc)}
                    <Button type="button" label="Subir documento" icon="pi pi-upload" className="p-button-sm" loading={subiendoDoc} onClick={subirDocumento} />
                </div>
                <DataTable value={documentos} loading={loading} showGridlines responsiveLayout="scroll" className="p-datatable-sm"
                    emptyMessage="Esta asamblea no tiene documentos cargados">
                    <Column field="titulo" header="Título" />
                    <Column header="Archivo" style={{ width: '9rem' }} body={(r) => linkArchivo(r.archivoUrl, r.archivoNombre)} />
                    <Column field="usuarioNombre" header="Subido por" style={{ width: '11rem' }} />
                    <Column header="Fecha" style={{ width: '8rem' }} body={(r) => fmtFecha(r.fechaAlta)} />
                    <Column header="" style={{ width: '4rem', textAlign: 'center' }} body={(r) => (
                        <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger"
                            tooltip="Eliminar" onClick={() => eliminarDocumento(r)} />
                    )} />
                </DataTable>
            </div>
        </div>
    );
};

const seccionTitulo = { fontWeight: 600, color: '#101828', marginBottom: '0.5rem' };
const formBox = { background: '#f9fafb', padding: '0.75rem', borderRadius: 8 };

export default AsambleaArchivos;
